import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { getDailyVerse } from '../data/verses';

const langs = [
  { key: 'english', label: 'English', flag: '🇬🇧' },
  { key: 'tamil', label: 'தமிழ்', flag: '🕊️' },
  { key: 'malayalam', label: 'മലയാളം', flag: '✝️' },
];

export default function DailyVerse() {
  const [active, setActive] = useState('english');
  const [verse, setVerse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVerse = async () => {
      // Use IST (India Standard Time, UTC+5:30) regardless of viewer's timezone
      const now = new Date();
      const istOffset = 5.5 * 60 * 60 * 1000; // UTC+5:30
      const istDate = new Date(now.getTime() + istOffset - (now.getTimezoneOffset() * 60000));
      // If UTC offset makes IST date different, use IST date
      const istStr = new Date(now.getTime() + (330 - now.getTimezoneOffset()) * 60000);
      const today = istStr.toISOString().split('T')[0];
      const { data: rows } = await supabase
        .from('daily_verses')
        .select('*')
        .eq('scheduled_date', today)
        .limit(1);
      const data = rows?.[0] || null;
      if (data) {
        setVerse({
          english: { text: data.english_text, ref: data.english_ref },
          tamil: { text: data.tamil_text, ref: data.tamil_ref },
          malayalam: { text: data.malayalam_text, ref: data.malayalam_ref },
        });
      } else {
        // Fallback to hardcoded rotation
        setVerse(getDailyVerse());
      }
      setLoading(false);
    };
    fetchVerse();
  }, []);

  // Display date in IST
  const istNow = new Date(new Date().getTime() + (330 - new Date().getTimezoneOffset()) * 60000);
  const today = istNow.toLocaleDateString('en-IN', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    timeZone: 'Asia/Kolkata',
  });

  if (loading) return (
    <section style={{ background: 'linear-gradient(135deg,#0a0f22,var(--bg-dark))', padding: '64px 20px', textAlign: 'center' }}>
      <div style={{ color: 'var(--primary)', fontFamily: "'Crimson Pro',serif", fontSize: '1.1rem' }}>Loading today's verse… 🙏</div>
    </section>
  );

  const current = verse?.[active];

  return (
    <section id="daily-verse" style={{ background: 'linear-gradient(135deg,#0a0f22 0%,var(--bg-dark) 50%,#120808 100%)', padding: '64px 20px', borderTop: '1px solid rgba(201,168,76,0.15)', borderBottom: '1px solid rgba(201,168,76,0.15)' }}>
      <div className="container" style={{ textAlign: 'center' }}>
        <span className="section-tag">✦ Daily Word</span>
        <h2 className="section-title">Today's <span>Bible Verse</span></h2>
        <div className="divider center" />
        <p style={{ fontSize: '0.78rem', color: 'rgba(201,168,76,0.7)', letterSpacing: '0.1em', marginBottom: 28 }}>📅 {today}</p>

        {/* Lang Tabs */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 32 }}>
          {langs.map((l) => (
            <button key={l.key} onClick={() => setActive(l.key)} style={{
              padding: '9px 22px', borderRadius: 40,
              border: active === l.key ? '2px solid var(--primary)' : '2px solid rgba(201,168,76,0.25)',
              background: active === l.key ? 'rgba(201,168,76,0.15)' : 'transparent',
              color: active === l.key ? 'var(--primary)' : 'var(--text-muted)',
              fontFamily: "'Raleway',sans-serif", fontWeight: 700,
              fontSize: '0.78rem', letterSpacing: '0.1em', cursor: 'pointer', transition: 'all 0.25s',
            }}>
              {l.flag} {l.label}
            </button>
          ))}
        </div>

        {/* Verse Card */}
        <div style={{ maxWidth: 720, margin: '0 auto', background: 'linear-gradient(160deg,rgba(201,168,76,0.07),rgba(13,27,62,0.9))', border: '1px solid rgba(201,168,76,0.28)', borderRadius: 20, padding: '44px 40px', position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
          <div style={{ position: 'absolute', top: -18, left: '50%', transform: 'translateX(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg,var(--primary),var(--primary-dark))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', boxShadow: '0 4px 16px rgba(201,168,76,0.4)' }}>✝</div>
          <p style={{ fontFamily: "'Crimson Pro',serif", fontStyle: 'italic', fontSize: 'clamp(1.15rem,3vw,1.5rem)', color: '#f0e8d8', lineHeight: 1.8, marginBottom: 22, textAlign: 'center' }}>
            "{current?.text}"
          </p>
          <div style={{ display: 'inline-block', background: 'linear-gradient(135deg,var(--primary),var(--primary-dark))', color: 'var(--bg-deep)', fontFamily: "'Cinzel Decorative',serif", fontSize: '0.82rem', fontWeight: 700, padding: '8px 22px', borderRadius: 40, letterSpacing: '0.08em' }}>
            — {current?.ref}
          </div>
        </div>

        {/* All 3 lang preview */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 16, maxWidth: 900, margin: '32px auto 0' }}>
          {langs.map((l, i) => (
            <div key={l.key} onClick={() => setActive(l.key)} style={{
              background: active === l.key ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${active === l.key ? 'rgba(201,168,76,0.5)' : 'rgba(201,168,76,0.12)'}`,
              borderRadius: 14, padding: '18px 20px', cursor: 'pointer', transition: 'all 0.25s', textAlign: 'left',
            }}>
              <div style={{ fontSize: '0.65rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--primary)', fontWeight: 700, marginBottom: 8 }}>{l.flag} {l.label}</div>
              <p style={{ fontFamily: "'Crimson Pro',serif", fontStyle: 'italic', fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                "{verse?.[l.key]?.text}"
              </p>
              <div style={{ fontSize: '0.72rem', color: 'rgba(201,168,76,0.7)', marginTop: 8 }}>{verse?.[l.key]?.ref}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
